import { Component, OnInit, OnDestroy, Input, Inject } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, switchMap, take } from 'rxjs/operators';
import get from 'lodash-es/get';

import { AuthenticationService } from '@finder/shared/services/authentication/authentication.service';
import { ConfigService } from '@finder/core/config.service';
import { ListService } from '@finder/features/list/services/list/list.service';
import { GetFullDateStringProvider, GET_FULL_DATE_STRING } from '@finder/shared/utils/string/getFullDateString';
import { GetDateObjectProvider, GET_DATE_OBJECT } from '@finder/shared/utils/date/getDateObject';
import { FinderApiConnectorService } from '@finder/shared/services/finder-api-connector/finder-api-connector.service';
import { SessionStorage } from '@finder/shared/services/session-storage/session-storage.service';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { UserSelectionStorage } from '@finder/shared/services/user-selection-storage/user-selection-storage.service';
import {
    DINE_CHECK_ANALYTICS,
    DINE_ANALYTICS_LINK_ID,
    EEC_STORAGE_KEY,
    FIND_DINE_LIST_AVAILABILITY
} from './dine-availability-filter.constants';
import { LOCALES, ANALYTICS_FACETS_UTILIZED_ACTION } from '@finder/shared/constants/app.constants';
import { IDineAvailabilityFilter, ITimeOption, ITimesConfig } from './dine-availability-filter-interface';
import { GetDaysBetweenDatesProvider, GET_DAYS_BETWEEN_DATES } from '@finder/shared/utils/date/getDaysBetweenDates';
import { GetDateStringProvider, GET_DATE_STRING } from '@finder/shared/utils/string/getDateString';
import { DatePickerWrapperComponent } from '@finder/shared/components/date-picker-wrapper/date-picker-wrapper.component';
import { InputComponent } from '@finder/shared/components/input/input.component';
import { RaSingleSelectComponent } from '@finder/shared/components/ra-wrappers/ra-single-select/ra-single-select.component';
import { RaCounterComponent } from '@finder/shared/components/ra-wrappers/ra-counter/ra-counter.component';
import { ButtonComponent } from '@finder/shared/components/button/button.component';
import { CommonModule } from '@angular/common';
import { LoadingSpinnerComponent } from '@finder/shared/components/loading-spinner/loading-spinner.component';
import { FontIconComponent } from '@finder/shared/components/font-icon/font-icon.component';

@Component({
    selector: 'finder-dine-availability-filter',
    templateUrl: './dine-availability-filter.component.html',
    styleUrls: ['./dine-availability-filter.component.scss'],
    standalone: true,
    imports: [
        DatePickerWrapperComponent,
        InputComponent,
        RaSingleSelectComponent,
        RaCounterComponent,
        ButtonComponent,
        CommonModule,
        LoadingSpinnerComponent,
        FontIconComponent,
    ]
})
export class DineAvailabilityFilterComponent implements OnInit, OnDestroy {

    @Input() diningFilter: IDineAvailabilityFilter;

    labels: any;
    error: string;
    timesConfig: ITimesConfig;
    partySizeLimit: number;

    selectedDateString: string;
    selectedDate: Date;
    selectedTime: string;
    selectedTimeLabel: string;
    selectedPartySize: number;

    isLoading = false;
    private destroy$ = new Subject<void>();

    constructor(
        private userSelection: UserSelectionStorage,
        private finderApi: FinderApiConnectorService,
        private authenticationService: AuthenticationService,
        private listService: ListService,
        private sessionStorage: SessionStorage,
        private finderAnalyticsService: FinderAnalyticsService,
        private configService: ConfigService,
        @Inject(GET_DATE_OBJECT) private getDateObject: GetDateObjectProvider,
        @Inject(GET_DAYS_BETWEEN_DATES) private getDaysBetweenDates: GetDaysBetweenDatesProvider,
        @Inject(GET_DATE_STRING) private getDateString: GetDateStringProvider,
        @Inject(GET_FULL_DATE_STRING) private getFullDateString: GetFullDateStringProvider,
    ) {}

    ngOnInit() {
        this.labels = this.diningFilter.labels;

        this.selectedDate = this.getSelectedDate();
        this.selectedDateString = this.getFullDateString(this.selectedDate);

        this.timesConfig = this.createTimesConfig(this.diningFilter.mealPeriods, this.diningFilter.times);
        this.selectedTime = this.getSelectedTime(this.timesConfig);
        this.selectedTimeLabel = this.getValidTime(this.timesConfig, this.selectedTime, 'value');
        this.partySizeLimit = this.diningFilter.partySize.limit;
        this.selectedPartySize = Number(this.userSelection.getPartySize()) || this.diningFilter.partySize.defaultValue;

        // trigger an availability search if the session key is set
        const findDineListAvailability = this.sessionStorage.get(FIND_DINE_LIST_AVAILABILITY);
        if (findDineListAvailability) {
            this.sessionStorage.remove(FIND_DINE_LIST_AVAILABILITY);
            this.findRestaurantAvailability(false);
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onDateChanged(event: Date) {
        this.selectedDate = event;
        this.selectedDateString = this.getFullDateString(event);

        this.timesConfig = this.createTimesConfig(this.diningFilter.mealPeriods, this.diningFilter.times);
        this.selectedTime = this.getValidTime(this.timesConfig, this.selectedTime);
    }

    onTimeSelected(event) {
        this.selectedTime = get(event, 'detail.key');
        this.selectedTimeLabel = get(event, 'detail.value');
    }

    onCounterChanged(event) {
        this.selectedPartySize = get(event, 'detail.value');
    }

    findRestaurantAvailability(analyticsTracking = true) {
        const dateString = this.getDateString(this.selectedDate);
        // store values used by the EEC SPA
        const eecCalendar = {
            createTime: new Date().getTime(),
            data: [dateString],
            ttl: '-1'
        };
        this.sessionStorage.set(EEC_STORAGE_KEY, JSON.stringify(eecCalendar));

        const selectedPartySizeString = this.selectedPartySize.toString();
        const searchDate = this.getDateObject(dateString).toLocaleDateString(
            LOCALES.en_US_normalized, {month: '2-digit', day: '2-digit', year: 'numeric'});
        const daysBetweenSearchDateAndToday = this.getDaysBetweenDates(this.selectedDate, new Date());

        if (analyticsTracking) {
            // We track user selection on the availability module.
            // We are using analyticsTracking parameter, to avoid firing it on page load,
            // we only want to track the user selection after clicking Search Times.
            // -linkId / prop9 / The user clicks on Search Times / "WDW_DineListing_SearchTimes"
            // -bookingSearchPartySize / prop18 / The user selected a specific Party Size / "2"
            // -bookingSearchArrivalDate / prop16 / The user selected a specific date / "08/16/20XX"
            // -bookingSearchDaysBetweenSearchAndArrival / prop15 / # of days between today and selected date /
            // If today is 08/01/2023 and user selected 08/10/2023 then the value will be "9".
            // -facetsUtilized / prop20 / The user selected a specific time / "06:00", "Brunch"
            this.finderAnalyticsService.updateModelAndTrack({
                linkId: `${this.configService.getValue('siteId').toUpperCase()}_${DINE_ANALYTICS_LINK_ID}`,
                bookingSearchPartySize: selectedPartySizeString,
                bookingSearchArrivalDate: searchDate,
                bookingSearchDaysBetweenSearchAndArrival: daysBetweenSearchDateAndToday,
                facetsUtilized:
                [{
                    name: this.selectedTimeLabel,
                    action: ANALYTICS_FACETS_UTILIZED_ACTION.add
                }],
                events: 'dineSearchStart'
            });
        }

        // save selected values
        this.userSelection.saveDate(this.selectedDate);
        this.userSelection.savePartySize(selectedPartySizeString);

        const searchParams: any = {};
        const searchByMealPeriod = this.diningFilter.mealPeriods.find(mealPeriod => mealPeriod.key === this.selectedTime);

        if (searchByMealPeriod) {
            searchParams.mealPeriod = this.selectedTime;
            this.userSelection.saveMealPeriod(this.selectedTime);
        } else {
            searchParams.searchTime = this.selectedTime;
            this.userSelection.saveTime(this.selectedTime);
        }

        this.error = null;
        this.isLoading = true;

        this.authenticationService.getSWID(false)
            .pipe(
                switchMap((swid: string) =>
                    this.finderApi.getDiningAvailabilityList(swid, dateString, this.selectedPartySize, searchParams)),
                take(1),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (data) => {
                    this.finderAnalyticsService.updateModelAndTrack({
                        linkId: DINE_CHECK_ANALYTICS.AVAIL_SUCCESS,
                        events: 'dineSearchSuccess',
                        bookingSearchArrivalDate: searchDate,
                        bookingSearchDaysBetweenSearchAndArrival: daysBetweenSearchDateAndToday,
                        bookingSearchPartySize: selectedPartySizeString,
                    });
                    this.listService.sortByAvailability(data.availability);
                },
                error: (error) => {
                    this.finderAnalyticsService.updateModelAndTrack({
                        linkId: DINE_CHECK_ANALYTICS.NO_AVAIL,
                        bookingSearchArrivalDate: searchDate,
                        bookingSearchDaysBetweenSearchAndArrival: daysBetweenSearchDateAndToday,
                        bookingSearchPartySize: selectedPartySizeString,
                    });
                    this.error = this.diningFilter.labels.error;
                }
            });
    }

    private getSelectedDate() {
        const currentDate = new Date();
        const storedDate = this.userSelection.getDate();

        if (!storedDate) {
            return currentDate;
        }

        const difference = this.getDaysBetweenDates(storedDate, currentDate);

        if (difference <= 0) {
            return currentDate;
        }

        return storedDate;
    }

    private createTimesConfig(mealPeriods: ITimeOption[], allDayTimes: ITimeOption[]): ITimesConfig {
        let availableTimes = allDayTimes;
        let firstTime = allDayTimes[0].key;

        const currentDate = new Date();
        const difference = this.getDaysBetweenDates(currentDate, this.selectedDate);

        if (difference >= 0) {
            firstTime = this.getNextTimeAvailable(currentDate);
            const firstTimeIndex = allDayTimes.findIndex(time => time.key === firstTime);
            // If closest 30min increment is within opening hours, show times starting from that index
            // Else if time is past closing time, don't show any times
            // Else, it is before opening, list all times
            if (firstTimeIndex !== -1) {
                availableTimes = allDayTimes.slice(firstTimeIndex);
            } else if (firstTime >= allDayTimes[allDayTimes.length - 1].key && firstTime <= '24:00:00') {
                availableTimes = [];
            }
        }
        const times = mealPeriods.concat(availableTimes);

        return {
            times,
            firstTime
        };
    }

    private getNextTimeAvailable(currentDate: Date) {
        const currentHour = currentDate.getHours();
        const currentMinutes = currentDate.getMinutes();
        // ensure hour has two digits
        let formattedHour = this.formatHour(currentHour);

        if (currentMinutes < 30) {
            return `${formattedHour}:30:00`;
        }

        formattedHour = this.formatHour(currentHour + 1);

        return `${formattedHour}:00:00`;
    }

    /**
     * @name formatHour
     * @description validate a numeric hour value and ensure it has 2-digits
     * example: 1 would return 01
     * @param hour  numeric hour value
     * @returns string
     */
    private formatHour(hour: number) {
        return `0${hour}`.slice(-2);
    }

    private getSelectedTime(timesConfig: ITimesConfig) {
        const storedTime = this.userSelection.getTime();

        if (!storedTime) {
            return timesConfig.firstTime;
        }

        return this.getValidTime(timesConfig, storedTime);
    }

    private getValidTime(timesConfig: ITimesConfig, selectedTime: string, getType: string = 'key') {
        const timeInList = timesConfig.times.find(time => time.key === selectedTime);

        return get(timeInList, getType, timesConfig.firstTime);
    }
}
